import { useFlag } from '@calendly/feature-check-react'
import { useBooleanState, useQueryParams } from '@calendly/hooks'
import { isEmpty } from 'lodash-es'
import moment from 'moment-timezone'
import { useContext, useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'

import { useGetUserQuery } from 'common'
import changeSet from 'core/change_set'
import { useGetMeetingIntelligenceUserSettingsQuery } from 'features/meeting_intelligence'
import { TRACKING_EVENT_NAMES, useTrack } from 'hooks/analytics_events'
import { timezoneSelector } from 'selectors/current_user'
import { isCalendlyMeeting } from 'shared/utils'

import { MeetingDetailDependenciesContext } from './meeting_detail_context'
import { MeetingDetailView } from './meeting_detail_view'

const useEventDayMeetings = (selectedEvent, dependencies) => {
  const timezone = useSelector(timezoneSelector)
  const { scopeType, scopeId } = dependencies.scope ?? {}

  const eventDate = selectedEvent?.startTime
    ? moment(selectedEvent.startTime).tz(timezone).format('YYYY-MM-DD')
    : null

  const order = eventDate && moment(eventDate).isSameOrAfter(moment(), 'day') ? 'ASC' : 'DESC'
  const past = eventDate && moment(eventDate).isBefore(moment(), 'day')
  const isEventToday = eventDate && moment().tz(timezone).format('YYYY-MM-DD') === eventDate

  // When event is today: use todayQueryArgs from context (feature passes Redux-derived value) for
  // RTK cache hit; otherwise build minimal args for fallback (e.g. meeting opened without list).
  // Avoid refetchOnMountOrArgChange when using shared todayQueryArgs: the list's useSearchData
  // already loaded this data. Triggering a refetch here would update the cache and cause the
  // meeting list to re-render/remount when today data comes back.
  const isUsingSharedTodayQuery = isEventToday && dependencies.todayQueryArgs != null

  const queryParams = isEventToday
    ? (dependencies.todayQueryArgs ?? {
      startDate: eventDate,
      endDate: eventDate,
      period: dependencies.Periods.UPCOMING,
      today: true,
      past: false,
      order: 'ASC',
      statusIds: [dependencies.Statuses.ACTIVE],
      scopeType,
      scopeId,
    })
    : {
      startDate: eventDate,
      endDate: eventDate,
      today: false,
      period: dependencies.Periods.FIXED,
      past,
      order,
      statusIds: [dependencies.Statuses.ACTIVE],
      scopeType,
      scopeId,
    }

  const { data } = dependencies.useGetMeetingsQuery(queryParams, {
    skip: !eventDate,
    refetchOnMountOrArgChange: !isUsingSharedTodayQuery,
  })

  const todayMeetings = data?.meetings?.edges
  return !isEmpty(todayMeetings) ? todayMeetings.map((meeting) => dependencies.mapNodeToMeeting(meeting.node, timezone)) : []
}

export const MeetingDetailWithContext = ({ context }) => {
  const { selectedEvent, handleClose, isLoading, updateActiveMeeting } = useContext(context)

  return (
    <MeetingDetail
      selectedEvent={selectedEvent}
      clearSelectedEvent={handleClose}
      updateSelectedEvent={updateActiveMeeting}
      isLoading={isLoading}
    />
  )
}

const MeetingDetail = ({
  selectedEvent,
  clearSelectedEvent,
  updateSelectedEvent,
  isLoading,
}) => {
  // Get dependencies from context
  const dependencies = useContext(MeetingDetailDependenciesContext)
  if (!dependencies) {
    throw new Error('MeetingDetail must be wrapped in a MeetingDetailDependenciesProvider')
  }

  const { data: currentUser } = useGetUserQuery()
  const track = useTrack()
  const [tempTab, setTempTab] = useState('')
  const [showReportModal, openReportModal, closeReportModal] = useBooleanState(false)
  const [showLeavePageModal, openLeavePageModal, closeLeavePageModal] = useBooleanState(false)
  const isPending = dependencies.isPendingEvent(selectedEvent)
  const isCalMeeting = selectedEvent && isCalendlyMeeting(selectedEvent)
  const history = useHistory()
  const params = new URLSearchParams(history.location.search)
  const hasMeetingIntelligence = useFlag('meeting_intelligence')
  const { tab, trigger_report: reportEventUUID } = useQueryParams()

  const [fetchSettings, doFetchSettings, skipFetchSettings] = useBooleanState(false)
  const { data: miSettings } = useGetMeetingIntelligenceUserSettingsQuery({}, { skip: !fetchSettings })

  // TODO: Brittle !tab conditional — we fetch MI settings when tab is undefined and event is external (!isCalMeeting).
  // Keep in sync with meeting_detail_view tab/content logic.
  useEffect(() => {
    if (selectedEvent && hasMeetingIntelligence && ((tab === 'notes') || (!tab && !isCalMeeting))) {
      doFetchSettings()
    } else {
      skipFetchSettings()
    }
  }, [hasMeetingIntelligence, isCalMeeting, selectedEvent, doFetchSettings, skipFetchSettings, tab])

  useEffect(() => {
    if (reportEventUUID && selectedEvent?.uuid === reportEventUUID) {
      openReportModal()
    }
  }, [selectedEvent, reportEventUUID, openReportModal])

  const todayMeetings = useEventDayMeetings(selectedEvent, dependencies)

  if (!selectedEvent && !isLoading) {
    return null
  }

  const switchToTab = (name) => {
    track({
      name: 'Navigation Link Clicked',
      properties: {
        navigation_link: name,
        navigation_location: 'meetings_page_right_rail',
        source_url: window.location.href,
      }
    })
  }

  const onNotesTabClick = () => {
    track({
      name: TRACKING_EVENT_NAMES.AI_NOTETAKER_ENGAGED,
      properties: {
        step: 'user_viewed_sidepanel_summary',
        event_uuid: selectedEvent.uuid,
        is_calendly_meeting: !selectedEvent.kind || selectedEvent.kind === 'CALENDLY',
        mi_event_uuid: selectedEvent?.meetingIntelligence?.uuid
      }
    })
  }

  const handleTabClick = (tabName) => {
    if (changeSet.isDirty()) {
      setTempTab(tabName)
      openLeavePageModal()
    } else {
      if (tabName === 'notes') onNotesTabClick()
      switchToTab(tabName)
      params.set('tab', tabName)
      history.replace({ search: params.toString() })
    }
  }

  const handleReportModalCancel = () => {
    closeReportModal()
  }

  const handleReportModalClose = () => {
    clearSelectedEvent()
    closeReportModal()
  }

  return (
    <MeetingDetailView
      selectedEvent={selectedEvent}
      isLoading={isLoading}
      tab={tab}
      todayMeetings={todayMeetings}
      currentUser={currentUser}
      miSettings={miSettings}
      isPending={isPending}
      isCalMeeting={isCalMeeting}
      showReportModal={showReportModal}
      tempTab={tempTab}
      setTempTab={setTempTab}
      showLeavePageModal={showLeavePageModal}
      onClose={clearSelectedEvent}
      onTabClick={handleTabClick}
      onReportModalOpen={openReportModal}
      onReportModalClose={handleReportModalClose}
      onReportModalCancel={handleReportModalCancel}
      onNotesTabClick={onNotesTabClick}
      onSwitchToTab={switchToTab}
      onCloseLeavePageModal={closeLeavePageModal}
      updateSelectedEvent={updateSelectedEvent}
    />
  )
}
